/**
 * Supabase認証コンテキスト
 */
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import type { User } from '../types';
import { authApi } from '../utils/api';

interface AuthContextType {
  currentUser: SupabaseUser | null;
  user: User | null;
  session: Session | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  /** 現在のパスワードで本人確認してからパスワードを変更する */
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithTwitter: () => Promise<void>;
  loginWithDiscord: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  /** バックエンドから user を再取得し真実源を更新する（mutation 成功時の整合に使用） */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<SupabaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // プロフィール取得の世代管理。fetchUserProfile を開始するたびに採番し、
  // await 解決後に自分が最新要求かを照合してから state を更新する。
  // これにより (a) SIGNED_OUT / セッション失効で通信中の取得結果が後から
  // ユーザーを復元してしまう stale auth state、(b) トークン付きイベント連続時に
  // 先行の in-flight 取得が後発の結果を上書きする last-write-wins レース、を防ぐ。
  const profileRequestIdRef = useRef(0);

  // バックエンドからユーザー情報を取得
  const fetchUserProfile = async () => {
    const requestId = ++profileRequestIdRef.current;
    try {
      const userData = await authApi.getCurrentUser();
      // 解決までの間に新しい取得要求／サインアウトで採番が進んでいたら、
      // この結果は失効しているため state を更新しない（stale 復元を防ぐ）。
      if (requestId !== profileRequestIdRef.current) return;
      setUser(userData);
    } catch (error) {
      // AxiosError をそのまま出力すると config.headers.Authorization（Bearer トークン）が
      // コンソールへ露出し得るため、ステータス/メッセージのみに絞ってログする。
      console.error(
        'Failed to fetch user profile:',
        axios.isAxiosError(error) ? (error.response?.status ?? error.message) : error,
      );
      // 失効した取得の失敗は現在の認証状態に反映しない（後発の要求／サインアウトが真実源）。
      if (requestId !== profileRequestIdRef.current) return;
      setUser(null);
      // バックエンドがトークンを拒否（401）した場合、Supabase セッションが残っていると
      // 再マウントのたびに /auth/me を叩いて 401 を繰り返す。セッションを破棄して
      // クリーンな未ログイン状態に戻し、ループを断ち切る。
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        await supabase.auth.signOut();
      }
    } finally {
      // 失効した要求は loading を触らない（最新要求が確定させる）。
      if (requestId === profileRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // メール・パスワードでサインアップ
  const signup = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) throw error;

    // メール確認が有効な場合、重複メールでも Supabase は error を返さず
    // data.user.identities を空配列で返す仕様。これを登録済みとして扱う。
    if (!data.user?.identities || data.user.identities.length === 0) {
      throw new Error('User already registered');
    }
  };

  // メール・パスワードでログイン
  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  // パスワード変更（メール・パスワード登録ユーザー向け）
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    const email = currentUser?.email;
    if (!email) {
      throw new Error('No authenticated user');
    }
    // Supabase の updateUser は現行パスワードを要求しないため、現在のパスワードで
    // 再認証して本人確認する（誤入力時は 'Invalid login credentials' が返る）。
    const { error: reauthError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });
    if (reauthError) throw reauthError;

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  // Google OAuth ログイン
  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) throw error;
  };

  // X (Twitter) OAuth ログイン
  const loginWithTwitter = async () => {
    // Supabaseでは provider=x を使用（twitterではなく）
    const redirectTo = encodeURIComponent(`${window.location.origin}/dashboard`);
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const authUrl = `${supabaseUrl}/auth/v1/authorize?provider=x&redirect_to=${redirectTo}`;

    window.location.href = authUrl;
  };

  // Discord OAuth ログイン
  const loginWithDiscord = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) throw error;
  };

  // ログアウト
  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // signOut の失敗でローカルの認証状態を残さない。特に退会直後はセッションの主体が
      // 既に存在せず失敗しうるため、ここで中断するとログイン済みのまま画面が残る。
      // 通常のログアウトでも、通信失敗が「押しても何も起きない」として現れるのを防ぐ。
      console.error('Sign out failed:', error);
    }
    localStorage.removeItem('access_token');
    setUser(null);
  };

  // アクセストークンを取得
  const getAccessToken = async (): Promise<string | null> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  };

  // Supabase認証状態の監視
  useEffect(() => {
    // defer 済みのプロフィール取得を解放するためのガード。
    // - aborted: 発火済みで実行待ちのラッパを無効化する
    // - pendingTimers: 未発火のタイマーを clearTimeout で破棄する
    let aborted = false;
    const pendingTimers = new Set<ReturnType<typeof setTimeout>>();
    const clearPendingProfileFetches = () => {
      pendingTimers.forEach(clearTimeout);
      pendingTimers.clear();
    };

    // 認証状態の変更を監視。onAuthStateChange はマウント直後に INITIAL_SESSION を発火するため、
    // これを初期化の唯一の起点とする（旧実装の getSession().then による初期取得は重複するため撤去し、
    // /auth/me の初期化二重呼び出しを解消する）。
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // 状態設定のみ即時同期実行する。onAuthStateChange のコールバックは Supabase の
      // auth ロック文脈で発火し得るため、コールバック内から supabase メソッドを再入呼び出ししない。
      setSession(session);
      setCurrentUser(session?.user ?? null);

      if (session?.access_token) {
        localStorage.setItem('access_token', session.access_token);
        // トークン付きイベント（INITIAL_SESSION / SIGNED_IN / TOKEN_REFRESHED）が
        // 連続した場合、未発火の先行予約を破棄して「最新イベントのみ defer」に統一する。
        // 既に発火済みで通信中の取得は fetchUserProfile 側の世代照合で無効化される。
        clearPendingProfileFetches();
        // fetchUserProfile は内部で 401 時に supabase.auth.signOut() を呼ぶため、
        // auth ロック文脈外（マクロタスク）へ退避してから実行し deadlock を回避する。
        const timer = setTimeout(() => {
          pendingTimers.delete(timer);
          if (aborted) return;
          fetchUserProfile();
        }, 0);
        pendingTimers.add(timer);
      } else {
        // セッション消失（SIGNED_OUT 等）時は、失効した先行セッション向けに予約済みの
        // プロフィール取得を破棄してから未ログイン状態へ落とす。未発火のタイマー破棄に加え、
        // 世代を進めて通信中（await 中）の取得結果が後からユーザーを復元するのを防ぐ。
        clearPendingProfileFetches();
        profileRequestIdRef.current++;
        localStorage.removeItem('access_token');
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      // 購読解除に加え、未発火の defer（clearTimeout）と発火済みラッパ（aborted）を解放し、
      // アンマウント後の余分な /auth/me 呼び出しを防ぐ。実行中リクエスト完了後の state 更新は
      // React 18 では no-op のため追加ガードは不要。
      aborted = true;
      clearPendingProfileFetches();
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    user,
    session,
    loading,
    signup,
    login,
    updatePassword,
    loginWithGoogle,
    loginWithTwitter,
    loginWithDiscord,
    logout,
    getAccessToken,
    refreshUser: fetchUserProfile
  };

  // children は常に描画し、ローディング中の画面出し分けは利用側（App.tsx の AppRoutes）に委ねる。
  // Provider 側でも children を止めると同じ責務が2箇所に分散し、どちらが効いているか追いにくくなるため、
  // ここでは loading を value で公開するだけに留める（表示の責務は UI 層に一本化）。
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
